/**
 * formatPlanType — THE ONE DISPLAY FORMATTER FOR A STORED INSURANCE PLAN TYPE.
 *
 * ═══ WHY THIS EXISTS, AND WHY IT IS A RENDERER ═══
 * The stored value is lowercase BY DESIGN and has to stay that way:
 *   · `useOnboardingStore` normalises what the card scanner extracts with
 *     `String(x).toLowerCase().replace(/[^a-z]/g, '')` before it is saved;
 *   · `utils/authRequired.js` decides whether a prior authorisation is required
 *     by comparing `(insurance.planType || '').toLowerCase() === 'hmo'`.
 * Re-casing what is stored would silently break that comparison, which is a
 * clinical-workflow decision, not a cosmetic one.
 *
 * ★★★ SO THE CAPITALISATION HAPPENS AT RENDER — and until v242.146.0 there was
 *     NO renderer at all. The pre-visit hand-off printed the raw column, so a
 *     physician's copy read `PLAN  ppo`. Same shape as SEX-CAPITALISE-SWEEP-2,
 *     one field over, and found the same way: by reading a real document
 *     against the account that produced it.
 *
 * ★★ PLAN TYPES ARE INITIALISMS, NOT WORDS. `ppo` is `PPO`, not `Ppo`. Anything
 *    not in the initialism set is title-cased rather than dropped — a renderer's
 *    job is to display what is stored, not to validate it — so a value nobody
 *    anticipated ('self-pay', 'commercial-insurance') still reads properly.
 *
 * Returns '' for an absent value, so a caller's own `|| 'Not provided'` fires.
 *
 * ★ Do not mint a second one of these. See utils/formatSex.js, whose second
 *   copy went unnoticed for a hundred versions.
 */

/** Plan types that are initialisms and must render fully upper-case. */
const INITIALISMS = new Set([
  'ppo', 'hmo', 'epo', 'pos', 'hdhp', 'pffs', 'snp', 'msa', 'fehb', 'chip', 'aco',
])

export const formatPlanType = (value) => {
  if (value == null) return ''
  const s = String(value).trim()
  if (!s) return ''
  const lower = s.toLowerCase()
  if (INITIALISMS.has(lower)) return lower.toUpperCase()
  // 'self-pay' -> 'Self-Pay', 'commercial insurance' -> 'Commercial Insurance'
  return s.replace(/\b[a-z]/g, (c) => c.toUpperCase())
}

export default formatPlanType
